import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, ArrowRight, MessageCircle } from "lucide-react";
import Logo from "./Logo";
import { useAuth } from "../context/AuthContext";
import { useSite } from "../context/SiteContext";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "./ui/sheet";

const LINKS = [
    { to: "/", label: "Home", end: true },
    { to: "/services", label: "Services" },
    { to: "/portfolio", label: "Work" },
    { to: "/courses", label: "Academy" },
    { to: "/resources", label: "Resources" },
    { to: "/about", label: "About Us" },
    { to: "/pricing", label: "Pricing" },
    { to: "/blog", label: "Blog" },
    { href: "https://bhajanmarket.muviarts.in/?utm_source=muviarts_nav&utm_medium=site", label: "Bhajan Store" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const { user, logout } = useAuth();
    const { openLead, openWhatsApp } = useSite();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled ? "bg-white/90 backdrop-blur-xl border-b border-[#e8e1d4]" : "bg-white border-b border-transparent"}`} data-testid="main-navbar">
            <div className="mx-container flex items-center justify-between h-[68px] gap-3">
                <Logo size="md" />

                <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
                    {LINKS.map((l) => l.href ? (
                        <a key={l.href} href={l.href} data-testid="nav-bhajan-store" className="relative text-sm font-semibold tracking-wide text-neutral-500 hover:text-ink"><span className="inline-flex flex-col items-center">{l.label}<span className="mt-1.5 h-[2px] w-5 rounded-full bg-transparent" /></span></a>
                    ) : (
                        <NavLink key={l.to} to={l.to} end={l.end} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                            className={({ isActive }) => `relative text-sm font-semibold tracking-wide transition-colors ${isActive ? "text-ink" : "text-neutral-500 hover:text-ink"}`}>
                            {({ isActive }) => (<span className="inline-flex flex-col items-center">{l.label}<span className={`mt-1.5 h-[2px] w-5 rounded-full transition-colors ${isActive ? "bg-brand-gold" : "bg-transparent"}`} /></span>)}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    {user ? (
                        <>
                            <Link to={user.role === "admin" ? "/admin" : "/dashboard"} className="text-sm font-semibold text-neutral-700 hover:text-ink" data-testid="nav-dashboard-link">{user.role === "admin" ? "Admin" : "My Learning"}</Link>
                            <button onClick={async () => { await logout(); navigate("/"); }} className="btn-outline !px-5 !py-2.5 text-xs" data-testid="nav-logout-btn">Log Out</button>
                        </>
                    ) : (
                        <Link to="/login" className="text-sm font-semibold text-neutral-700 hover:text-ink" data-testid="nav-login-link">Log In</Link>
                    )}
                    <button onClick={() => openWhatsApp?.("launch")} className="btn-outline !px-3 !py-2.5" aria-label="WhatsApp" data-testid="nav-whatsapp"><MessageCircle className="h-4 w-4 text-[#25D366]" /></button>
                    <button onClick={() => openLead?.("General")} className="btn-primary !px-5 !py-2.5 text-xs whitespace-nowrap" data-testid="nav-start-project">Start Your Project</button>
                </div>

                <div className="lg:hidden flex items-center gap-1.5 shrink-0">
                    <button onClick={() => openLead?.("General")} className="btn-primary !px-3.5 !py-2 text-[11px] whitespace-nowrap" data-testid="nav-mobile-start">Start Project</button>
                    <Sheet>
                        <SheetTrigger asChild><button className="p-2 -mr-1" aria-label="Open menu" data-testid="mobile-menu-btn"><Menu className="h-6 w-6" /></button></SheetTrigger>
                        <SheetContent side="right" className="w-[86%] sm:w-[380px] p-0 bg-white">
                            <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                            <div className="px-6 py-6 border-b border-[#e8e1d4]"><Logo size="md" withTagline /></div>
                            <nav className="flex flex-col px-6 py-4">
                                {LINKS.map((l) => l.href ? (
                                    <a key={l.href} href={l.href} data-testid="mobile-nav-bhajan-store" className="py-3 text-lg font-display font-semibold border-b border-[#f0ead9] text-ink">{l.label}</a>
                                ) : (
                                    <SheetClose asChild key={l.to}>
                                        <NavLink to={l.to} end={l.end} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                                            className={({ isActive }) => `py-3 text-lg font-display font-semibold border-b border-[#f0ead9] ${isActive ? "text-brand-gold" : "text-ink"}`}>{l.label}</NavLink>
                                    </SheetClose>
                                ))}
                            </nav>
                            <div className="px-6 mt-4 flex flex-col gap-3">
                                {user ? (
                                    <>
                                        <SheetClose asChild><Link to={user.role === "admin" ? "/admin" : "/dashboard"} className="btn-dark w-full" data-testid="mobile-dashboard-link">{user.role === "admin" ? "Admin" : "My Learning"}</Link></SheetClose>
                                        <button onClick={async () => { await logout(); navigate("/"); }} className="btn-outline w-full" data-testid="mobile-logout-btn">Log Out</button>
                                    </>
                                ) : (
                                    <SheetClose asChild><Link to="/login" className="btn-outline w-full" data-testid="mobile-login-btn">Log In</Link></SheetClose>
                                )}
                                <button onClick={() => openWhatsApp?.("launch")} className="btn w-full bg-[#25D366] text-white px-7 py-4" data-testid="mobile-whatsapp"><MessageCircle className="h-4 w-4" /> WhatsApp</button>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
}
